// MemoriesPanel.jsx

import { useState } from "react";
import { Plus, Trash2, Edit2 } from "lucide-react";

import { useMemoriesData } from "@/hooks/useMemoriesData";

export default function MemoriesPanel() {
  const { memories, add, edit, remove } = useMemoriesData();
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState({ content: "", category: "idea" });

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ content: "", category: "idea" });

  function addMemory() {
    if (!draft.content.trim()) return;

    add(draft.content, draft.category);
    setDraft({ content: "", category: "idea" });
    setIsAdding(false);
  }

  function handleAddKey(e) {
    if (e.key === "Enter") addMemory();
    if (e.key === "Escape") setIsAdding(false);
  }

  function startEdit(memory) {
    setEditingId(memory.id);
    setEditDraft({
      content: memory.content,
      category: memory.category,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft({ content: "", category: "idea" });
  }

  function saveEdit(id) {
    edit(id, editDraft);
    cancelEdit();
  }

  function handleEditKey(e, id) {
    if (e.key === "Enter") saveEdit(id);
    if (e.key === "Escape") cancelEdit();
  }

  function deleteMemory(id) {
    remove(id);
  }

  return (
    <div className="console-card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="uppercase text-sm tracking-wider">Memories</span>
          <span className="console-badge">{memories.length}</span>
        </div>

        <button
          onClick={() => setIsAdding((v) => !v)}
          className="w-9 h-9 rounded-full border border-cyan-400/40 bg-black/40 flex items-center justify-center"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Add */}
      {isAdding && (
        <div className="mb-4 space-y-2">
          <textarea
            className="w-full px-4 py-2 rounded bg-black/40 border border-white/10 resize-none"
            rows={3}
            placeholder="Write the memory..."
            value={draft.content}
            onChange={(e) =>
              setDraft({ ...draft, content: e.target.value })
            }
            onKeyDown={handleAddKey}
            autoFocus
          />

          <div className="flex gap-2">
            <select
              className="px-3 py-2 rounded-full text-xs bg-black/60 border border-white/20"
              value={draft.category}
              onChange={(e) =>
                setDraft({ ...draft, category: e.target.value })
              }
            >
              <option value="identity">identity</option>
              <option value="preference">preference</option>
              <option value="relationship">relationship</option>
              <option value="health">health</option>
              <option value="work">work</option>
              <option value="project">project</option>
              <option value="habit">habit</option>
              <option value="idea">idea</option>
              <option value="history">history</option>
              <option value="constraint">constraint</option>
            </select>

            <button
              onClick={addMemory}
              className="px-4 py-2 text-xs text-green-400 border border-green-400/60 rounded-full"
            >
              Save
            </button>

            <button
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 text-xs text-red-400 border border-red-400/60 rounded-full"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {memories.map((memory) => (
          <div
            key={memory.id}
            className="p-3 rounded bg-black/30 border border-white/5 flex justify-between gap-3"
          >
            <div className="flex-1">
              {editingId === memory.id ? (
                <>
                  <textarea
                    className="w-full bg-black/40 border border-white/10 rounded p-2 text-sm resize-none"
                    rows={2}
                    value={editDraft.content}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, content: e.target.value })
                    }
                    onKeyDown={(e) => handleEditKey(e, memory.id)}
                    autoFocus
                  />

                  <select
                    className="mt-2 px-3 py-2 rounded-full text-xs bg-black/60 border border-white/20"
                    value={editDraft.category}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, category: e.target.value })
                    }
                  >
                    <option value="identity">identity</option>
                    <option value="preference">preference</option>
                    <option value="relationship">relationship</option>
                    <option value="health">health</option>
                    <option value="work">work</option>
                    <option value="project">project</option>
                    <option value="habit">habit</option>
                    <option value="idea">idea</option>
                    <option value="history">history</option>
                    <option value="constraint">constraint</option>
                  </select>
                </>
              ) : (
                <>
                  <div className="text-sm mb-1">{memory.content}</div>
                  <div className="text-[11px] text-soft uppercase">
                    {memory.category}
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => startEdit(memory)}>
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => deleteMemory(memory.id)}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 